import { useMemo, useState } from 'react';
import { Button, Container, Spinner } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconBrandWhatsapp,
  IconCalendar,
  IconCheck,
  IconDownload,
  IconHome,
  IconMessage,
  IconSparkles,
  IconTrendingUp,
} from '@tabler/icons-react';
import useMonthlyPreSaleReport, {
  MonthlyReport,
} from './hooks/useMonthlyPreSaleReport';
import {
  OBJECTION_OPTIONS,
  PROSPECT_STAGE_OPTIONS,
  optionLabel,
} from './constants/preSaleFeedbackOptions';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto',
  'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const SHORT_MONTHS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const monthLabel = (value: string) => {
  const [year, month] = value.split('-');
  return `${MONTH_NAMES[Number(month) - 1]} ${year}`;
};

// Últimos 12 meses (el actual incluido) como YYYY-MM.
const buildMonthOptions = () => {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return { value, label: monthLabel(value) };
  });
};

const signed = (value: number | null, suffix = '') =>
  value === null ? '—' : `${value > 0 ? '+' : ''}${value}${suffix}`;

const Bars = ({
  rows,
  tone = 'green',
}: {
  rows: { label: string; percentage: number }[];
  tone?: 'green' | 'blue';
}) => (
  <div className="rp-bars">
    {rows.map((row) => (
      <div key={row.label} className="rp-bar">
        <div className="rp-bar__head">
          <span>{row.label}</span>
          <b>{row.percentage}%</b>
        </div>
        <div className="rp-bar__track">
          <div
            className={`rp-bar__fill rp-bar__fill--${tone}`}
            style={{ width: `${Math.min(100, row.percentage)}%` }}
          />
        </div>
      </div>
    ))}
  </div>
);

const Card = ({
  title,
  children,
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={`vd-card ${className}`}>
    <h2 className="vd-card__title mb-3">{title}</h2>
    {children}
  </section>
);

const shareText = (report: MonthlyReport) =>
  [
    `Reporte mensual de preventa · ${report.developer.name} · ${monthLabel(report.month)}`,
    `Asesorías del mes: ${report.totals.visits}`,
    report.executive_summary,
    report.recommendation ? `Recomendación: ${report.recommendation}` : null,
  ]
    .filter(Boolean)
    .join('\n\n');

// Aviso según `ai_status` (por qué hay o no textos de IA).
const AiNotice = ({
  report,
  onRegenerate,
}: {
  report: MonthlyReport;
  onRegenerate: () => void;
}) => {
  if (report.ai_status === 'not_enough_data') {
    return (
      <section className="rp-notice">
        <IconMessage size={18} />
        <span>
          Este mes no tiene feedback de los Propers; solo se muestran las
          asesorías realizadas.
        </span>
      </section>
    );
  }
  if (report.ai_status === 'failed') {
    return (
      <section className="rp-notice rp-notice--warn rp-noprint">
        <IconSparkles size={18} />
        <span>
          Los textos con IA no están disponibles ahora. Se reintentarán en la
          próxima consulta, o puedes pedirlos de nuevo.
        </span>
        <button type="button" className="rp-btn rp-btn--ghost" onClick={onRegenerate}>
          Regenerar textos
        </button>
      </section>
    );
  }
  return null;
};

const ReportBody = ({ report }: { report: MonthlyReport }) => {
  const { totals, interest, top_model: topModel } = report;
  const topObjections = report.objections.map((o) => ({
    label: optionLabel(OBJECTION_OPTIONS, o.key),
    percentage: o.percentage,
  }));
  const funnel = report.funnel.map((f) => ({
    label: optionLabel(PROSPECT_STAGE_OPTIONS, f.key),
    percentage: f.percentage,
  }));
  const trendValues = interest.trend.map((t) => t.hot_clients_percentage ?? 0);
  const trendMax = Math.max(1, ...trendValues);

  return (
    <>
      {report.executive_summary && (
        <Card title="Resumen ejecutivo">
          <p className="rp-text">{report.executive_summary}</p>
        </Card>
      )}

      <div className="rp-kpis">
        <section className="vd-card">
          <span className="vd-icon vd-icon--green">
            <IconCalendar size={20} />
          </span>
          <div className="rp-kpi__label">Asesorías del mes</div>
          <div className="rp-kpi__value">{totals.visits}</div>
          <div className="rp-kpi__sub">
            {signed(totals.delta)} vs. mes anterior
          </div>
        </section>
        <section className="vd-card">
          <span className="vd-icon vd-icon--orange">
            <IconMessage size={20} />
          </span>
          <div className="rp-kpi__label">Top 3 objeciones</div>
          <div className="rp-kpi__value rp-kpi__value--text">
            {topObjections.length
              ? topObjections.map((o) => o.label).join(' · ')
              : '—'}
          </div>
          <div className="rp-kpi__sub">Reportadas por los Propers</div>
        </section>
        <section className="vd-card">
          <span className="vd-icon vd-icon--blue">
            <IconHome size={20} />
          </span>
          <div className="rp-kpi__label">Tipología más solicitada</div>
          <div className="rp-kpi__value rp-kpi__value--text">
            {topModel ? topModel.name : '—'}
          </div>
          <div className="rp-kpi__sub">
            {topModel ? `${topModel.percentage}% de las asesorías` : 'Sin datos'}
          </div>
        </section>
        <section className="vd-card">
          <span className="vd-icon vd-icon--green">
            <IconTrendingUp size={20} />
          </span>
          <div className="rp-kpi__label">Interés vs. mes anterior</div>
          <div className="rp-kpi__value rp-kpi__value--accent">
            {signed(interest.hot_clients_change_percentage, '%')}
          </div>
          <div className="rp-kpi__sub">
            {interest.hot_clients_change_percentage === null
              ? 'Sin datos del mes anterior'
              : 'Clientes “calientes”'}
          </div>
        </section>
      </div>

      <div className="rp-grid">
        <Card title="Objeciones cruzadas">
          {topObjections.length ? (
            <Bars rows={topObjections} />
          ) : (
            <p className="text-muted mb-0">Sin objeciones registradas.</p>
          )}
        </Card>

        <Card title="Tendencia de interés">
          <div className="rp-trend">
            {interest.trend.map((point, index) => {
              const value = point.hot_clients_percentage;
              const isLast = index === interest.trend.length - 1;
              return (
                <div key={point.month} className="rp-trend__col">
                  <div
                    className={`rp-trend__bar${isLast ? ' is-last' : ''}${value === null ? ' is-empty' : ''}`}
                    style={{ height: `${value === null ? 8 : Math.max(8, (value / trendMax) * 100)}%` }}
                    title={value === null ? 'Sin feedback' : `${value}%`}
                  />
                  <span>{SHORT_MONTHS[Number(point.month.split('-')[1]) - 1]}</span>
                </div>
              );
            })}
          </div>
          {interest.trend_summary && (
            <p className="rp-text rp-text--muted mt-3 mb-0">
              {interest.trend_summary}
            </p>
          )}
        </Card>

        <Card title="Competencia mencionada">
          {report.competitors.items.length ? (
            <div className="rp-chips">
              {report.competitors.items.map((c) => (
                <span key={c.name} className="rp-chip">
                  {c.name} <small>{c.percentage}%</small>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0">Sin competencia mencionada.</p>
          )}
          {report.competitors.summary && (
            <p className="rp-text rp-text--muted mt-3 mb-0">
              {report.competitors.summary}
            </p>
          )}
        </Card>

        <Card title="Dónde se traba el funnel">
          {funnel.length ? (
            <Bars rows={funnel} tone="blue" />
          ) : (
            <p className="text-muted mb-0">Sin datos del embudo.</p>
          )}
        </Card>
      </div>

      {report.recommendation && (
        <section className="rp-reco">
          <span className="rp-reco__icon">
            <IconCheck size={22} />
          </span>
          <div>
            <h2 className="vd-card__title mb-2">Recomendación de Propi</h2>
            <p className="rp-text mb-0">{report.recommendation}</p>
          </div>
        </section>
      )}
    </>
  );
};

const MonthlyPreSaleReport = () => {
  const navigate = useNavigate();
  const monthOptions = useMemo(buildMonthOptions, []);
  // Por defecto, el último mes completo.
  const [month, setMonth] = useState(monthOptions[1].value);
  const [developerId, setDeveloperId] = useState('');
  const { developers, state, report, regenerate } = useMonthlyPreSaleReport(
    month,
    developerId,
  );

  const goBack = () => navigate('/?tab=pre-sale');
  const share = () => {
    if (!report) return;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareText(report))}`,
      '_blank',
      'noopener',
    );
  };
  // El PDF se genera con el diálogo de impresión del navegador ("Guardar como PDF").
  const download = () => window.print();

  return (
    <Container className="mt-6 visit-detail-v2 rp-page" fluid>
      <Button className="vd-back rp-noprint" onClick={goBack}>
        <IconArrowLeft size={16} /> Volver a PreVenta
      </Button>

      <header className="rp-header">
        <div>
          {report?.ai_status === 'generated' && (
            <span className="vd-pill vd-pill--success mb-2">
              <IconSparkles size={12} /> Generado con IA
            </span>
          )}
          <h1 className="vd-hero__title">Reporte mensual de preventa</h1>
          <p className="rp-text rp-text--muted mb-0">
            Resumen de asesorías y percepción de clientes para tu desarrolladora.
          </p>
        </div>
        {state === 'ready' && (
          <button
            type="button"
            className="dv2-create-btn rp-noprint"
            onClick={download}
          >
            <IconDownload size={16} /> Descargar reporte
          </button>
        )}
      </header>

      <div className="rp-filters rp-noprint">
        <label>
          <span>Mes</span>
          <select
            className="rp-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Desarrolladora</span>
          <select
            className="rp-select"
            value={developerId}
            onChange={(e) => setDeveloperId(e.target.value)}
          >
            <option value="">Selecciona una desarrolladora</option>
            {developers.map((developer) => (
              <option key={developer.id} value={developer.id}>
                {developer.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state === 'idle' && (
        <section className="vd-card">
          <p className="mb-0 text-muted">
            Elige una desarrolladora para ver su reporte del mes.
          </p>
        </section>
      )}
      {state === 'loading' && (
        <section className="vd-card rp-loading">
          <Spinner color="success" />
          <div>
            <b>Preparando el reporte…</b>
            <p className="mb-0 text-muted">
              La primera vez que se consulta un mes puede tardar unos segundos,
              mientras se generan los textos con IA.
            </p>
          </div>
        </section>
      )}
      {state === 'no-data' && (
        <section className="vd-card">
          <p className="mb-0 text-muted">
            Este mes no tiene asesorías finalizadas ni feedback de los Propers
            para esta desarrolladora, así que todavía no hay reporte.
          </p>
        </section>
      )}
      {state === 'no-developer' && (
        <section className="vd-card">
          <p className="mb-0 text-muted">
            No se encontró la desarrolladora seleccionada.
          </p>
        </section>
      )}
      {state === 'error' && (
        <section className="vd-card">
          <p className="mb-0 text-muted">
            No se pudo cargar el reporte. Intenta de nuevo en unos momentos.
          </p>
        </section>
      )}

      {state === 'ready' && report && (
        <>
          <AiNotice report={report} onRegenerate={regenerate} />
          <ReportBody report={report} />
          <div className="rp-actions rp-noprint">
            <button type="button" className="rp-btn rp-btn--dark" onClick={download}>
              <IconDownload size={16} /> Descargar reporte
            </button>
            {report.ai_status === 'generated' && (
              <button
                type="button"
                className="rp-btn rp-btn--ghost"
                onClick={regenerate}
                title="Vuelve a generar los textos con IA (puede tardar)"
              >
                <IconSparkles size={16} /> Regenerar textos
              </button>
            )}
            <button type="button" className="dv2-create-btn" onClick={share}>
              <IconBrandWhatsapp size={18} /> Compartir por WhatsApp
            </button>
          </div>
          <p className="rp-footnote">
            Todo dato individual está agregado o anonimizado.
          </p>
        </>
      )}
    </Container>
  );
};

export default MonthlyPreSaleReport;
